import React, { useRef, useState, useEffect, useCallback, Fragment } from 'react';
import T from 'prop-types';
import { find, includes, reject, without, isEmpty, values, flatten, isEqual, filter } from 'lodash-es';
import qs from 'qs';

import SearchTypeahead from '../../../../components/SearchTypeahead';
import CheckboxGroup from '../../../../components/CheckboxGroup';
import NestedSelector from '../../../../components/NestedSelector';
import NewFeatureRequestButton from '../../../../components/NewFeatureRequestButton';
import Button from '../../../../components/Buttons/Button';
import FinanceMenuItems from '../../../../components/TypeaheadItems/FinanceMenuItems';
import FinanceMenuFooter from '../../../../components/TypeaheadItems/FinanceMenuFooter';
import {
  Menu,
  MenuGroup,
  MenuDivider,
  MenuButton,
  AccordionSubMenu,
  useMenuProps,
} from '../../../../components/DropdownSet';
import { useIsInitialRender } from '../../../../hooks';

const defaultAsset = {
  companies: [],
  geographies: [],
  gics_sectors: [],
  market_caps: [],
};

const renderCustomMenuItems = ({ items, renderItem }) => (
  <FinanceMenuItems
    items={items}
    selectedAsset="companies"
    renderItem={renderItem}
  />
);

const Filters = ({
  filters,
  handleChange,
  filtersOptions,
  clearList,
  type,
  resetSearchEvent,
  disabledLibrary,
  isLoggedUser,
  freeSampleReportId,
  eventId,
  isLimitedFinanceCustomer,
}) => {
  const menuInstanceRef = useRef();
  const searchRef = useRef(null);
  const isInitialRender = useIsInitialRender();

  const [filtersState, setFiltersState] = useState(defaultAsset);
  const [result, setSearchResult] = useState([]);

  const handleSearch = searchResults => setSearchResult(searchResults);

  const searchTypeaheadRef = () => searchRef.current?.typeaheadInstance();

  const programmaticallyCloseMemu = () => {
    if (menuInstanceRef.current) menuInstanceRef.current.closeMenu();
  };

  const handleSelectCompany = (selectedCompany) => {
    if (!selectedCompany) return;
    const selectedCompanies = filtersState.companies || [];
    const selected = [...selectedCompanies,
      { ref_no: selectedCompany.ref_no, name: selectedCompany.name }];

    setFiltersState({ ...filtersState, companies: selected });
    if (searchRef.current) {
      searchTypeaheadRef().clear();
      searchTypeaheadRef().focus();
    }
  };

  const handleCompanySelected = (selectedCompany) => {
    const selectedCompanies = filtersState.companies || [];
    const itemSelected = !!find(selectedCompanies, company => (
      company.ref_no.toString() === selectedCompany.ref_no.toString()));
    const showReplaceButton = false;
    return { itemSelected, showReplaceButton };
  };

  const handleRemoveTag = (refNo) => {
    const selectedData = reject(filtersState.companies, entity => entity.ref_no === refNo);

    setFiltersState({ ...filtersState, companies: selectedData });
  };

  const handleClearClick = () => {
    if (searchRef.current) {
      searchRef.current.setState({ query: '' });
      if (searchTypeaheadRef()) {
        searchTypeaheadRef().clear();
      }
    }
    setFiltersState(defaultAsset);
    handleChange(defaultAsset);
    clearList();
    programmaticallyCloseMemu();
  };

  const handleCheckboxChange = (inputKey, value) => {
    const activeValues = filtersState[inputKey];
    const active = includes(activeValues, value)
      ? without(activeValues, value)
      : [...activeValues, value];
    setFiltersState({ ...filtersState, [inputKey]: active });
  };

  const handleGicsChange = useCallback((selected) => {
    setFiltersState(prevFiltersState => ({ ...prevFiltersState, gics_sectors: selected }));
  }, []);

  const handleApplyClick = () => {
    resetSearchEvent();
    handleChange({
      ...filters,
      ...filtersState,
    });
    programmaticallyCloseMemu();
  };

  const isEmptyState = isEmpty(flatten(values(filtersState)));

  const isEmptyFilters = isEmpty(flatten(values(filters)));

  const stateEqualFilters = isEqual(filtersState, filters);

  const applyDisabled = (isEmptyState && isEmptyFilters) || stateEqualFilters;

  const cancelDisabled = type.value === 'all'
    && ((isEmptyFilters && stateEqualFilters) || (isEmptyState && !stateEqualFilters));

  // Trigger fetchResearchLibraryData on type change
  useEffect(() => {
    if (!isInitialRender && type.value) {
      if (!applyDisabled) handleApplyClick();
      else handleChange(filters);
    }
  }, [type.value]);

  const renderTag = (label, refNo) => (
    <div
      key={refNo}
      className="text-black filter-search-item"
    >
      <i className="hicon-close-2 text-12" onClick={() => handleRemoveTag(refNo)} />
      {label}
    </div>
  );

  const menuProps = useMenuProps({
    menuStyle: { width: 310 },
    overflow: 'auto',
    portal: false,
  });

  return (
    <div className="filter">
      <Menu
        menuButton={(
          <MenuButton
            variant="light"
            caret={false}
          >
            <i className="hicon-sliders-h-thin" />
            Filters
          </MenuButton>
        )}
        setDownOverflow
        instanceRef={menuInstanceRef}
        {...menuProps}
      >
        {disabledLibrary ? (
          <div className="research-library-filters-content">
            <div className="research-library-filters-disabled">
              <h2 className="research-library-filters-disabled-header">
                Research Library
              </h2>
              <h3 className="research-library-filters-disabled-subheader">
                Get actionable information on mobile&apos;s most relevant tickers. Apptopia&apos;s
                &nbsp;Wall Street-trained research team publishes all of their mobile insights here.
              </h3>
              <ul className="research-library-filters-disabled-list">
                <li>10+ new reports per month</li>
                <li>Regular updates on key sectors</li>
                <li>Company-wide access for one fee</li>
              </ul>
              {isLoggedUser && (
                <div className="research-library-filters-disabled-request-btn">
                  <NewFeatureRequestButton
                    featureName="Research Library"
                    buttonText="Request Access"
                    buttonClassName="hms-btn hms-btn--primary request-btn"
                    showErrorInTooltip
                  />
                </div>
              )}
              {isLoggedUser && freeSampleReportId > 0 && (
                <div className="research-library-filters-disabled-link">
                  <a href={`/research_library/download?${qs.stringify({ research_library: { event_id: eventId, id: freeSampleReportId } })}`}>Download Latest Earnings Preview</a>
                </div>
              )}
            </div>
          </div>
        ) : (
          <Fragment>
            <div className="research-library-filters-content">
              <div className="filter-container-search-box">
                <SearchTypeahead
                  autoFocus={false}
                  searchAsset="companies"
                  itemSelected={handleCompanySelected}
                  maxHeight="550px"
                  placeholder="Search companies"
                  menuStyles={{ maxWidth: '56rem' }}
                  ref={searchRef}
                  clearButton
                  customMenuFooter={(isLimitedFinanceCustomer && result.length > 0)
                    ? <FinanceMenuFooter asset="companies" />
                    : undefined}
                  renderCustomMenuItems={isLimitedFinanceCustomer ? renderCustomMenuItems : undefined}
                  decorateItems={data => filter(data, record => !isEmpty(record.ticker))}
                  onChange={items => handleSelectCompany(items[0])}
                  onSearchCallback={isLimitedFinanceCustomer ? handleSearch : undefined}
                />
                <div className="filter-search-box">
                  {(filtersState.companies).map(company => renderTag(company.name, company.ref_no))}
                </div>
              </div>
              <MenuGroup takeOverflow>
                <div className="filter-container-scroll show-scrollbar">
                  <div className="filter-container">
                    <AccordionSubMenu
                      key="market-cap"
                      open={false}
                      label="Market Cap"
                    >
                      <CheckboxGroup
                        id="market-cap-checkboxes"
                        className="d-flex flex-column"
                        options={filtersOptions.marketCapOptions}
                        active={filtersState.market_caps}
                        gapCheckbox="10px"
                        onChange={(_, value) => handleCheckboxChange('market_caps', value)}
                      />
                    </AccordionSubMenu>
                  </div>
                  <MenuDivider />
                  <div className="filter-container">
                    <AccordionSubMenu
                      key="geography"
                      open={false}
                      label="Geography"
                    >
                      <CheckboxGroup
                        id="geography-checkboxes"
                        className="d-flex flex-column"
                        options={filtersOptions.geographyOptions}
                        active={filtersState.geographies}
                        gapCheckbox="10px"
                        onChange={(_, value) => handleCheckboxChange('geographies', value)}
                      />
                    </AccordionSubMenu>
                  </div>
                  <MenuDivider />
                  <div className="filter-container">
                    <AccordionSubMenu
                      key="finance-sectors"
                      open={false}
                      label="Finance Sectors"
                    >
                      <NestedSelector
                        options={filtersOptions.gicsSectorsOptions}
                        selectedOptions={filtersState.gics_sectors}
                        searchPlaceholder="Search GICS Sectors"
                        showSelectectedList
                        onChange={handleGicsChange}
                      />
                    </AccordionSubMenu>
                  </div>
                  <MenuDivider />
                </div>
              </MenuGroup>
            </div>
            <div className="research-library-filters-head">
              <Button
                variant="primary"
                size="lg"
                disabled={applyDisabled}
                onClick={() => handleApplyClick()}
              >
                Apply Filters
              </Button>

              <Button
                variant="secondary"
                size="lg"
                disabled={cancelDisabled}
                onClick={() => handleClearClick()}
              >
                Clear All Filters
              </Button>
            </div>
          </Fragment>
        )}
      </Menu>
    </div>
  );
};

Filters.propTypes = {
  filters: T.object.isRequired,
  handleChange: T.func.isRequired,
  clearList: T.func.isRequired,
  resetSearchEvent: T.func.isRequired,
  type: T.object.isRequired,
  isLoggedUser: T.bool.isRequired,
  disabledLibrary: T.bool,
  filtersOptions: T.shape({
    geographyOptions: T.array,
    reportTypesOptions: T.array,
    gicsSectorsOptions: T.array,
    marketCapOptions: T.array,
  }).isRequired,
  freeSampleReportId: T.number,
  eventId: T.number,
  isLimitedFinanceCustomer: T.bool,
};

export default Filters;
